import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import {
  HOSTNAME_THEMES,
  ThemeProvider,
  ThemeType,
  applyColorMode,
  getStoredColorMode,
} from './components/theme-provider';
import { queryClient } from './lib/query-client';
import './i18n/config';
import './app/globals.css';

const getTheme = (): ThemeType => {
  // Own origins: the hostname decides, and it also refreshes the stored value so
  // getDashboardUrl()/getBrandName() (which read localStorage) agree with it.
  const fromHost = HOSTNAME_THEMES[window.location.hostname];
  if (fromHost) {
    localStorage.setItem('theme', fromHost);
    return fromHost;
  }

  // Shared origin (/whatsapp/ proxy) or localhost: trust what the dashboard stored.
  const stored = localStorage.getItem('theme') as ThemeType | null;
  if (stored && Object.values(ThemeType).includes(stored)) return stored;

  localStorage.setItem('theme', ThemeType.MENTEO_THEME);
  return ThemeType.MENTEO_THEME;
};

// Paint the right mode before the first render, so a dark-mode user never sees
// a white flash while React boots.
applyColorMode(getStoredColorMode());

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={getTheme()}>
          <App />
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </StrictMode>,
);
